import React, { useEffect, useMemo, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ImageIcon from "@mui/icons-material/Image";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import WineBarIcon from "@mui/icons-material/WineBar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Photo from "./post/PhotoExtension";
import Tasting from "./post/TastingExtension";
import IngredientGroup from "./post/IngredientGroupExtension";
import EditPhotoModal from "./post/EditPhotoModal";
import EditIngredientGroupModal from "./post/EditIngredientGroupModal";
import {
  EDITORIAL_TEMPLATES,
  normalizeComposition,
  normalizeVisibility,
  VISIBILITY_OPTIONS,
} from "../post/compositions";
import {
  bodyHasContent,
  extractIngredientGroupsFromEditor,
  htmlForEditor,
  stripIgroupContent,
} from "../utils/postContent";
import { getSettings, listTastings } from "../utils/apiClient";

const starterKitConfig = {
  heading: false,
  bulletList: false,
  orderedList: false,
  listItem: false,
  blockquote: false,
  codeBlock: false,
  code: false,
  horizontalRule: false,
};

function EditorToolbar({
  editor,
  busy,
  onAddPhoto,
  onAddIngredientGroup,
  onAddTasting,
  showIngredientGroup,
}) {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        px: 0.5,
        py: 0.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        flexWrap: "wrap",
      }}
    >
      <IconButton
        size="small"
        aria-label="Fed"
        disabled={!editor}
        color={editor?.isActive("bold") ? "primary" : "default"}
        onClick={() => editor?.chain().focus().toggleBold().run()}
      >
        <FormatBoldIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Kursiv"
        disabled={!editor}
        color={editor?.isActive("italic") ? "primary" : "default"}
        onClick={() => editor?.chain().focus().toggleItalic().run()}
      >
        <FormatItalicIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Understreget"
        disabled={!editor}
        color={editor?.isActive("underline") ? "primary" : "default"}
        onClick={() => editor?.chain().focus().toggleUnderline().run()}
      >
        <FormatUnderlinedIcon fontSize="small" />
      </IconButton>
      {onAddPhoto ? (
        <IconButton
          size="small"
          aria-label="Tilføj foto"
          disabled={!editor || busy}
          onClick={onAddPhoto}
        >
          <ImageIcon fontSize="small" />
        </IconButton>
      ) : null}
      {onAddTasting ? (
        <IconButton
          size="small"
          aria-label="Indsæt smagning"
          disabled={!editor || busy}
          onClick={onAddTasting}
          title="Indsæt smagning"
        >
          <WineBarIcon fontSize="small" />
        </IconButton>
      ) : null}
      {showIngredientGroup ? (
        <IconButton
          size="small"
          aria-label="Tilføj ingrediensgruppe"
          disabled={!editor || busy}
          onClick={onAddIngredientGroup}
          title="Tilføj ingrediensgruppe"
        >
          <PlaylistAddIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Stack>
  );
}

const editorShellSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "& .tiptap": {
    outline: "none",
    minHeight: 140,
    fontFamily: "inherit",
    lineHeight: 1.7,
  },
  "& .tiptap p": { m: 0, mb: 1.5 },
  "& .tiptap p.is-editor-empty:first-of-type::before": {
    color: "text.disabled",
    content: "attr(data-placeholder)",
    float: "left",
    height: 0,
    pointerEvents: "none",
  },
  "& .post-photo-node": { my: 1 },
  "& .post-igroup-node": { my: 1.5 },
};

function PostForm({
  initial = {},
  onSubmit,
  onCancel,
  onDelete,
  onTitleChange,
  submitLabel = "Gem",
  postUuid,
  recipeUuid,
  postType = "post",
  hideTemplate = false,
}) {
  const isRecipe = postType === "recipe";
  const initialTitle = initial.title || "";
  const [title, setTitle] = useState(initialTitle);
  const [titleTouched, setTitleTouched] = useState(Boolean(initialTitle.trim()));
  const [visibility, setVisibility] = useState(
    normalizeVisibility(initial.visibility)
  );
  const [composition, setComposition] = useState(
    normalizeComposition(initial.composition, { postType })
  );
  const [tags, setTags] = useState(
    Array.isArray(initial.tags) ? initial.tags.join(", ") : initial.tags || ""
  );
  const [yieldAmount, setYieldAmount] = useState(initial.yield_amount || "");
  const [cookTime, setCookTime] = useState(initial.cook_time || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoTarget, setPhotoTarget] = useState("body");
  const [igroupOpen, setIgroupOpen] = useState(false);
  const [tastingOpen, setTastingOpen] = useState(false);
  const [tastingChoices, setTastingChoices] = useState([]);
  const [tastingLoading, setTastingLoading] = useState(false);
  const [bodyTick, setBodyTick] = useState(0);
  const [instructionsTick, setInstructionsTick] = useState(0);
  const [allowedVisibilities, setAllowedVisibilities] = useState(
    VISIBILITY_OPTIONS.map((o) => o.value)
  );

  const topicKey = isRecipe ? "recipes" : "home";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSettings();
        if (cancelled) return;
        const allowed = data?.topic_visibility?.[topicKey];
        if (Array.isArray(allowed) && allowed.length) {
          setAllowedVisibilities(allowed);
        }
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [topicKey]);

  const visibilityOptions = useMemo(() => {
    const allowed = new Set(allowedVisibilities);
    if (visibility) allowed.add(visibility);
    return VISIBILITY_OPTIONS.filter((opt) => allowed.has(opt.value));
  }, [allowedVisibilities, visibility]);

  useEffect(() => {
    if (!visibilityOptions.some((opt) => opt.value === visibility)) {
      const fallback = visibilityOptions[0]?.value || "private";
      setVisibility(fallback);
    }
  }, [visibilityOptions, visibility]);

  const initialHtml = useMemo(
    () =>
      htmlForEditor(
        isRecipe ? stripIgroupContent(initial.body || "") : initial.body || "",
        initial.photos || [],
        [],
        initial.tastings || []
      ),
    [initial.body, initial.photos, initial.tastings, isRecipe]
  );

  const initialInstructionsHtml = useMemo(
    () =>
      htmlForEditor(
        initial.instructions || "",
        initial.photos || [],
        isRecipe ? initial.ingredient_groups || [] : []
      ),
    [initial.instructions, initial.photos, initial.ingredient_groups, isRecipe]
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure(starterKitConfig),
      Underline,
      Placeholder.configure({
        placeholder: isRecipe ? "Skriv historien / intro…" : "Skriv dit indlæg…",
      }),
      Photo,
      ...(isRecipe ? [] : [Tasting]),
    ],
    content: initialHtml,
    onUpdate: () => setBodyTick((n) => n + 1),
  });

  const instructionsEditor = useEditor(
    {
      extensions: [
        StarterKit.configure(starterKitConfig),
        Underline,
        Placeholder.configure({
          placeholder: "Skriv instruktioner / fremgangsmåde…",
        }),
        Photo,
        IngredientGroup,
      ],
      content: initialInstructionsHtml,
      onUpdate: () => setInstructionsTick((n) => n + 1),
    },
    [isRecipe]
  );

  useEffect(() => {
    onTitleChange?.(title);
  }, [title, onTitleChange]);

  const bodyHtml = editor?.getHTML?.() || "";
  const instructionsHtml = instructionsEditor?.getHTML?.() || "";
  void bodyTick;
  void instructionsTick;
  const titleOk = Boolean(title.trim()) && titleTouched;
  const hasContent = isRecipe
    ? bodyHasContent(bodyHtml) || bodyHasContent(instructionsHtml)
    : bodyHasContent(bodyHtml);
  const canSave = titleOk && hasContent;
  const busy = saving || deleting;

  const handleTitleChange = (event) => {
    setTitle(event.target.value);
    setTitleTouched(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!title.trim()) {
      setError("Tilføj en titel før du gemmer.");
      return;
    }
    if (!titleTouched) {
      setError("Rediger titlen før du gemmer.");
      return;
    }
    const body = isRecipe
      ? stripIgroupContent(editor?.getHTML?.() || "")
      : editor?.getHTML?.() || "";
    const instructions = isRecipe ? instructionsEditor?.getHTML?.() || "" : undefined;
    if (!bodyHasContent(body) && !(isRecipe && bodyHasContent(instructions))) {
      setError(
        isRecipe
          ? "Tilføj historie, instruktioner, et foto eller en ingrediensgruppe."
          : "Tilføj noget tekst eller et foto."
      );
      return;
    }
    setSaving(true);
    setError("");
    try {
      const ingredient_groups = isRecipe
        ? extractIngredientGroupsFromEditor(instructionsEditor)
            .map((g) => ({
              group_uuid: g.group_uuid,
              name: g.name,
              items: (g.items || [])
                .filter(
                  (item) =>
                    (item.ingredient_name || "").trim() &&
                    item.amount !== "" &&
                    item.amount != null
                )
                .map((item) => ({
                  ingredient_uuid: item.ingredient_uuid || undefined,
                  ingredient_name: (item.ingredient_name || "").trim(),
                  amount: Number(item.amount),
                  unit: item.unit || "gram",
                  note: (item.note || "").trim() || null,
                })),
            }))
            .filter((g) => g.name && g.items.length)
        : undefined;
      await onSubmit({
        title: title.trim(),
        body,
        visibility: normalizeVisibility(visibility),
        ...(isRecipe
          ? {
              instructions,
              yield_amount: yieldAmount.trim() || null,
              cook_time: cookTime.trim() || null,
              ingredient_groups,
            }
          : {
              composition: normalizeComposition(composition, { postType }),
            }),
        tags,
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Gemning mislykkedes");
    } finally {
      setSaving(false);
    }
  };

  const handleAddPhoto = (photo) => {
    if (photoTarget === "instructions") {
      instructionsEditor?.chain().focus().insertPhoto(photo).run();
    } else {
      editor?.chain().focus().insertPhoto(photo).run();
    }
  };

  const handleAddIngredientGroup = (group) => {
    instructionsEditor?.chain().focus().insertIngredientGroup(group).run();
  };

  const openTastingPicker = async () => {
    setTastingOpen(true);
    setTastingLoading(true);
    try {
      const data = await listTastings();
      setTastingChoices(Array.isArray(data) ? data : []);
    } catch {
      setTastingChoices([]);
    } finally {
      setTastingLoading(false);
    }
  };

  const handleInsertTasting = (tasting) => {
    editor?.chain().focus().insertTasting({
      tastingUuid: tasting.tasting_uuid,
      title: tasting.title || "Smagning",
    }).run();
    setTastingOpen(false);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2, minHeight: 0, flex: 1 }}
    >
      {error ? <Typography color="error">{error}</Typography> : null}

      <InputBase
        value={title}
        onChange={handleTitleChange}
        placeholder="Titel"
        disabled={busy}
        fullWidth
        multiline
        inputProps={{
          maxLength: 200,
          "aria-label": "Titel",
        }}
        sx={{
          fontWeight: 700,
          letterSpacing: "-0.03em",
          fontSize: { xs: "1.75rem", sm: "2.25rem" },
          lineHeight: 1.25,
          color: "text.primary",
          "& .MuiInputBase-input": {
            p: 0,
          },
          "& .MuiInputBase-input::placeholder": {
            opacity: 0.35,
            fontWeight: 700,
          },
        }}
      />

      {isRecipe ? (
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Historie
        </Typography>
      ) : null}

      <Box
        sx={{
          ...editorShellSx,
          flex: isRecipe ? "0 0 auto" : 1,
          minHeight: { xs: 200, sm: isRecipe ? 240 : 320 },
        }}
      >
        <EditorToolbar
          editor={editor}
          busy={busy}
          onAddPhoto={() => {
            setPhotoTarget("body");
            setPhotoOpen(true);
          }}
          onAddTasting={isRecipe ? undefined : openTastingPicker}
        />
        <Box sx={{ flex: 1, overflow: "auto", px: 1.5, py: 1.5 }}>
          <EditorContent editor={editor} className="tiptap" />
        </Box>
      </Box>

      {isRecipe ? (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              label="Mængde"
              value={yieldAmount}
              onChange={(e) => setYieldAmount(e.target.value)}
              placeholder="f.eks. 10 pølser"
              disabled={busy}
              fullWidth
              size="small"
            />
            <TextField
              label="Tilberedningstid"
              value={cookTime}
              onChange={(e) => setCookTime(e.target.value)}
              placeholder="f.eks. 45 min"
              disabled={busy}
              fullWidth
              size="small"
            />
          </Stack>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Instruktioner
          </Typography>
          <Box
            sx={{
              ...editorShellSx,
              flex: "0 0 auto",
              minHeight: { xs: 200, sm: 240 },
            }}
          >
            <EditorToolbar
              editor={instructionsEditor}
              busy={busy}
              showIngredientGroup
              onAddPhoto={() => {
                setPhotoTarget("instructions");
                setPhotoOpen(true);
              }}
              onAddIngredientGroup={() => setIgroupOpen(true)}
            />
            <Box sx={{ flex: 1, overflow: "auto", px: 1.5, py: 1.5 }}>
              <EditorContent editor={instructionsEditor} className="tiptap" />
            </Box>
          </Box>
        </>
      ) : null}

      <TextField
        label="Tags"
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        placeholder="rejse, noter, familie"
        helperText="Kommasepareret"
        disabled={busy}
        fullWidth
        size="small"
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          {!hideTemplate && !isRecipe ? (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="editorial-template-label">Skabelon</InputLabel>
              <Select
                labelId="editorial-template-label"
                label="Skabelon"
                value={composition}
                onChange={(e) => setComposition(e.target.value)}
                disabled={busy}
              >
                {EDITORIAL_TEMPLATES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="visibility-label">Synlighed</InputLabel>
            <Select
              labelId="visibility-label"
              label="Synlighed"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              disabled={busy}
            >
              {visibilityOptions.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {onDelete ? (
            <Button
              type="button"
              color="error"
              variant="text"
              disabled={busy}
              onClick={async () => {
                if (!window.confirm("Slet dette indlæg? Det kan ikke fortrydes.")) return;
                setDeleting(true);
                setError("");
                try {
                  await onDelete();
                } catch (err) {
                  const detail = err.response?.data?.detail;
                  setError(
                    typeof detail === "string" ? detail : err.message || "Sletning mislykkedes"
                  );
                  setDeleting(false);
                }
              }}
            >
              {deleting ? "Sletter…" : "Slet"}
            </Button>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          {onCancel ? (
            <Button type="button" variant="text" onClick={onCancel} disabled={busy}>
              Annuller
            </Button>
          ) : null}
          <Button type="submit" variant="contained" disabled={busy || !canSave}>
            {saving ? "Gemmer…" : submitLabel}
          </Button>
        </Stack>
      </Stack>

      <EditPhotoModal
        open={photoOpen}
        onClose={() => setPhotoOpen(false)}
        mode="add"
        postUuid={postUuid}
        recipeUuid={recipeUuid}
        onSaved={handleAddPhoto}
      />
      {isRecipe ? (
        <EditIngredientGroupModal
          open={igroupOpen}
          onClose={() => setIgroupOpen(false)}
          mode="add"
          onSaved={handleAddIngredientGroup}
        />
      ) : null}
      {!isRecipe ? (
        <Dialog open={tastingOpen} onClose={() => setTastingOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Indsæt smagning</DialogTitle>
          <DialogContent dividers>
            {tastingLoading ? (
              <Typography variant="body2" color="text.secondary">
                Henter smagninger…
              </Typography>
            ) : tastingChoices.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Ingen smagninger endnu. Opret en under Vinsmagning.
              </Typography>
            ) : (
              <List dense>
                {tastingChoices.map((t) => (
                  <ListItemButton key={t.tasting_uuid} onClick={() => handleInsertTasting(t)}>
                    <ListItemText
                      primary={t.title}
                      secondary={
                        t.created_at
                          ? new Date(t.created_at).toLocaleDateString()
                          : undefined
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </DialogContent>
        </Dialog>
      ) : null}
    </Box>
  );
}

export default PostForm;
